/**
 * Workshop API client for outline development system
 */

import { api } from './client';

/**
 * Create or get a workshop session
 * @param {Object} params
 * @param {string} params.storyId - Story UUID
 * @param {string} params.template - Workshop template (workshop_movement, etc.)
 * @param {string} [params.parentSectionId] - Parent section for nested workshops
 * @param {string} [params.name] - Optional session name
 * @returns {Promise<{data: Object, created: boolean}>}
 */
export async function createOrGetWorkshop({ storyId, template, parentSectionId, name }) {
  return api.post('/workshops', {
    story_id: storyId,
    template,
    parent_section_id: parentSectionId,
    name
  });
}

/**
 * Get workshop session with sections
 * @param {string} sessionId - Workshop session UUID
 * @returns {Promise<{data: {session: Object, sections: Array}}>}
 */
export async function getWorkshop(sessionId) {
  return api.get(`/workshops/${sessionId}`);
}

/**
 * Create a section from workshop
 * @param {string} sessionId - Workshop session UUID
 * @param {Object} params
 * @param {string} params.displayName - Section title
 * @param {string} [params.description] - Section description
 * @param {number} [params.sequenceOrder] - Order in sequence
 * @returns {Promise<{data: Object}>}
 */
export async function createSectionFromWorkshop(sessionId, { displayName, description, sequenceOrder }) {
  // TODO(#384): callers (useSequencesWorkshop) also pass `parentSectionId` and
  // `sectionType`, but they are inert — destructured away here, and the backend
  // route derives both from the workshop session (outline_section_id + template)
  // and ignores any request-supplied parent/type. Left as-is deliberately for
  // this PR (guard + coercion fix only); drop the dead args from the callers in a
  // separate cosmetic cleanup so this diff stays scoped.
  return api.post(`/workshops/${sessionId}/sections`, {
    display_name: displayName,
    description,
    sequence_order: sequenceOrder
  });
}

/**
 * Remove a section from workshop
 * @param {string} sessionId - Workshop session UUID
 * @param {string} sectionId - Section UUID to remove
 * @returns {Promise<{data: {deleted: boolean}}>}
 */
export async function removeSectionFromWorkshop(sessionId, sectionId) {
  return api.delete(`/workshops/${sessionId}/sections/${sectionId}`);
}

/**
 * Get outline map for a story
 * @param {string} storyId - Story UUID
 * @returns {Promise<{data: {outline: Object|null, sections: Array, logline: string|null}}>}
 */
export async function getOutlineMap(storyId) {
  return api.get(`/stories/${storyId}/outline-map`);
}

/**
 * Reorder a section
 * @param {string} sectionId - Section UUID
 * @param {number} newOrder - New sequence order
 * @returns {Promise<{data: {reordered: boolean}}>}
 */
export async function reorderSection(sectionId, newOrder) {
  return api.put(`/outline-sections/${sectionId}/reorder?new_order=${newOrder}`, {});
}

/**
 * Get available frameworks
 *
 * @deprecated Use `getFrameworks()` from `api/frameworks.js` instead.
 * This endpoint returns a flat list without category grouping.
 * Migration: Import from 'api/frameworks' and use the new grouped response.
 * This function will be removed once frontend migration is complete.
 *
 * @returns {Promise<{data: Array<{key: string, name: string, description: string, medium: string, section_count: number}>}>}
 */
export async function getFrameworks() {
  return api.get('/workshops/frameworks');
}

/**
 * Get acts from a framework
 *
 * @deprecated Use `getFramework(key)` from `api/frameworks.js` instead.
 * This endpoint returns only acts with title/description fields.
 * The new endpoint returns full framework definition with name/purpose fields
 * and includes description, examples, unit_label, and category.
 * Migration: Import from 'api/frameworks' and access response.units
 * This function will be removed once frontend migration is complete.
 *
 * @param {string} frameworkKey - Framework key
 * @returns {Promise<{data: Array<{title: string, description: string}>}>}
 */
export async function getFrameworkActs(frameworkKey) {
  return api.get(`/workshops/frameworks/${frameworkKey}`);
}

/**
 * Generate acts for a story using a framework
 * @param {string} storyId - Story UUID
 * @param {Object} [options] - Generation options
 * @param {boolean} [options.regenerate=false] - Whether to use existing acts as context
 * @param {string} [options.frameworkKey] - Framework key to use for generation (e.g., 'hero_journey', 'three_act')
 * @returns {Promise<{data: Array<{title: string, description: string}>}>}
 */
export async function generateActs(storyId, options = {}) {
  // Support legacy signature: generateActs(storyId, regenerate)
  const opts = typeof options === 'boolean' ? { regenerate: options } : options;
  const { regenerate = false, frameworkKey } = opts;

  const body = { regenerate };
  if (frameworkKey) {
    body.framework_key = frameworkKey;
  }

  return api.post(`/stories/${storyId}/acts/generate`, body);
}

/**
 * Create a new blank act for a story
 * @param {string} storyId - Story UUID
 * @param {string} [title='New Act'] - Act title
 * @param {string} [description=''] - Act description
 * @returns {Promise<{data: Object}>}
 */
export async function createAct(storyId, title = 'New Act', description = '') {
  return api.post(`/stories/${storyId}/acts`, { title, description });
}

/**
 * Delete all acts for a story
 * Used before regenerating to prevent act accumulation
 * @param {string} storyId - Story UUID
 * @returns {Promise<{data: {deleted_count: number}}>}
 */
export async function deleteAllActs(storyId) {
  return api.delete(`/stories/${storyId}/acts`);
}

/**
 * Get act generation history for a story
 * @param {string} storyId - Story UUID
 * @param {number} [limit=50] - Maximum number of entries to return
 * @returns {Promise<{data: Array<{id: string, acts: Array, source: string, source_session_id: string|null, created_at: string}>}>}
 */
export async function getActHistory(storyId, limit = 50) {
  return api.get(`/stories/${storyId}/acts/history?limit=${limit}`);
}

/**
 * Save current acts to history
 * @param {string} storyId - Story UUID
 * @param {Array<{title: string, description: string}>} acts - Acts to save
 * @param {string} [source='manual'] - Source of the acts ('generated' or 'manual')
 * @param {string} [sourceSessionId] - Optional session ID that triggered this
 * @returns {Promise<{data: {id: string, acts: Array, source: string, created_at: string}}>}
 */
export async function saveActHistory(storyId, acts, source = 'manual', sourceSessionId = null) {
  return api.post(`/stories/${storyId}/acts/history`, {
    acts,
    source,
    source_session_id: sourceSessionId
  });
}

export const workshopsApi = {
  createOrGetWorkshop,
  getWorkshop,
  createSectionFromWorkshop,
  removeSectionFromWorkshop,
  getOutlineMap,
  reorderSection,
  getFrameworks,
  getFrameworkActs,
  generateActs,
  createAct,
  deleteAllActs,
  getActHistory,
  saveActHistory
};

export default workshopsApi;
